import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TechIcon = ({ icon: Icon, name, color, delay = 0 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        if (showTooltip && containerRef.current && tooltipRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const padding = 12; // padding from screen edge

            // Calculate ideal centered position
            let leftOffset = (containerRect.width - tooltipRect.width) / 2;

            // Check if tooltip goes off the right edge
            const tooltipRight = containerRect.left + leftOffset + tooltipRect.width;
            if (tooltipRight > viewportWidth - padding) {
                leftOffset = viewportWidth - padding - containerRect.left - tooltipRect.width;
            }

            // Check if tooltip goes off the left edge
            const tooltipLeft = containerRect.left + leftOffset;
            if (tooltipLeft < padding) {
                leftOffset = padding - containerRect.left;
            }

            setTooltipStyle({ left: `${leftOffset}px` });
        }
    }, [showTooltip]);

    return (
        <motion.div
            ref={containerRef}
            className="relative"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay }}
        >
            <motion.div
                className="tech-icon cursor-pointer outline-none"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onFocus={() => setShowTooltip(true)}
                onBlur={() => setShowTooltip(false)}
                whileHover={{
                    scale: 1.1,
                    rotate: 5,
                }}
                whileTap={{ scale: 0.95 }}
                tabIndex={0}
                role="img"
                aria-label={name}
            >
                <Icon
                    className="text-3xl md:text-4xl transition-colors duration-300"
                    style={{ color: color }}
                />
            </motion.div>

            <AnimatePresence>
                {showTooltip && (
                    <motion.div
                        ref={tooltipRef}
                        className="absolute -top-10 z-50"
                        style={tooltipStyle}
                        initial={{ opacity: 0, y: 5, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.9 }}
                        transition={{ duration: 0.15 }}
                    >
                        <span className="px-3 py-1.5 text-xs font-medium text-white bg-bg-elevated border border-white/10 rounded-[12px] whitespace-nowrap shadow-lg">
                            {name}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default TechIcon;
